export default {
    name: "SettingsAssignmentModal",

    props: {
        is_open: {
            type: Boolean,
            default: false
        },
        is_saving: {
            type: Boolean,
            default: false
        },
        form_error: {
            type: String,
            default: ""
        },
        selected_assignment_id: {
            type: [Number, String, null],
            default: null
        },
        active_properties: {
            type: Array,
            default: () => []
        },
        municipalities: {
            type: Array,
            default: () => []
        },
        tariff_books: {
            type: Array,
            default: () => []
        },
        tariffs: {
            type: Array,
            default: () => []
        },
        tariff_form: {
            type: Object,
            required: true
        },
        is_property_locked: {
            type: Boolean,
            default: false
        }
    },

    emits: ["close", "submit", "update:tariffForm"],

    data() {
        return ({
            field_errors: {},
            is_form_error_dismissed: false,
            snackbar_timeout_id: null
        });
    },

    watch: {
        is_open(next_value) {
            if (next_value) {
                this.field_errors = {};
                this.is_form_error_dismissed = false;
            }
        },

        form_error(message) {
            this.is_form_error_dismissed = false;

            if (message) {
                this.start_snackbar_timer();
            }
        }
    },

    computed: {
        visible_form_error() {
            return this.form_error && !this.is_form_error_dismissed ? this.form_error : "";
        },

        submit_button_label() {
            if (this.is_saving) {
                return "Saving...";
            }

            return this.selected_assignment_id ? "Save changes" : "Assign";
        },

        selected_property() {
            return this.active_properties.find((property) => Number(property.id) === Number(this.tariff_form.property_id)) || null;
        },

        municipality_options() {
            return this.municipalities
                .filter((municipality) => municipality?.id && municipality?.name)
                .sort((first_municipality, second_municipality) => {
                    return String(first_municipality.name).localeCompare(String(second_municipality.name));
                });
        },

        selected_municipality_id() {
            const municipality_id = Number(this.tariff_form.municipality_id);
            return Number.isFinite(municipality_id) && municipality_id > 0 ? municipality_id : null;
        },

        filtered_tariff_books() {
            if (!this.selected_municipality_id) {
                return [...this.tariff_books].sort((first_tariff_book, second_tariff_book) => {
                    return String(first_tariff_book?.name || "").localeCompare(String(second_tariff_book?.name || ""));
                });
            }

            const tariffs_with_municipality = this.tariffs.filter((tariff) => {
                const municipality_id = Number(tariff.municipality_id);
                return Number.isFinite(municipality_id) && municipality_id > 0;
            });
            const matching_tariff_book_ids = new Set(
                tariffs_with_municipality
                    .filter((tariff) => Number(tariff.municipality_id) === this.selected_municipality_id)
                    .map((tariff) => Number(tariff.tariff_book_id))
                    .filter((tariff_book_id) => Number.isFinite(tariff_book_id))
            );

            const matching_tariff_books = this.tariff_books.filter((tariff_book) => {
                return matching_tariff_book_ids.has(Number(tariff_book.id));
            });

            const tariff_books_to_show = matching_tariff_books.length || !tariffs_with_municipality.length
                ? matching_tariff_books
                : [];

            return [...(tariff_books_to_show.length ? tariff_books_to_show : this.tariff_books)].sort((first_tariff_book, second_tariff_book) => {
                return String(first_tariff_book?.name || "").localeCompare(String(second_tariff_book?.name || ""));
            });
        },

        filtered_tariffs() {
            if (!this.tariff_form.tariff_book_id) {
                return [];
            }

            return this.tariffs.filter((tariff) => {
                return Number(tariff.tariff_book_id) === Number(this.tariff_form.tariff_book_id);
            });
        }
    },

    beforeUnmount() {
        this.clear_snackbar_timer();
    },

    methods: {
        clear_snackbar_timer() {
            if (this.snackbar_timeout_id) {
                clearTimeout(this.snackbar_timeout_id);
                this.snackbar_timeout_id = null;
            }
        },

        start_snackbar_timer() {
            this.clear_snackbar_timer();
            this.snackbar_timeout_id = setTimeout(() => {
                this.is_form_error_dismissed = true;
                this.snackbar_timeout_id = null;
            }, 5000);
        },

        clear_form_error() {
            this.clear_snackbar_timer();
            this.is_form_error_dismissed = true;
        },

        close_modal() {
            this.$emit("close");
        },

        submit_form() {
            const field_errors = this.validate_form_fields();

            if (Object.keys(field_errors).length) {
                this.field_errors = field_errors;
                this.$nextTick(this.focus_first_invalid_field);
                return;
            }

            this.field_errors = {};
            this.$emit("submit");
        },

        validate_form_fields() {
            const errors = {};

            if (!this.is_property_locked && !this.tariff_form.property_id) {
                errors.property_id = "Property is required.";
            }

            if (!this.tariff_form.tariff_book_id) {
                errors.tariff_book_id = "Tariff book is required.";
            }

            if (!this.tariff_form.tariff_id) {
                errors.tariff_id = "Tariff selection is required.";
            }

            return errors;
        },

        focus_first_invalid_field() {
            if (this.field_errors.property_id && this.$refs.property_select) {
                this.$refs.property_select.focus();
                return;
            }

            if (this.field_errors.tariff_book_id && this.$refs.tariff_book_select) {
                this.$refs.tariff_book_select.focus();
                return;
            }

            if (this.field_errors.tariff_id && this.$refs.tariff_select) {
                this.$refs.tariff_select.focus();
            }
        },

        update_municipality_input(field_value) {
            const normalized_value = String(field_value || "").trim().toLowerCase();
            const matching_municipality = this.municipality_options.find((municipality) => {
                return String(municipality.name || "").trim().toLowerCase() === normalized_value;
            }) || null;

            const next_form_state = {
                ...this.tariff_form,
                municipality_search: field_value,
                municipality_id: matching_municipality?.id || "",
                tariff_book_id: "",
                tariff_id: ""
            };

            this.$emit("update:tariffForm", {
                ...next_form_state
            });
        },

        get_selected_tariff(tariff_id) {
            return this.tariffs.find((tariff) => Number(tariff.id) === Number(tariff_id)) || null;
        },

        get_tariff_municipality_id(tariff) {
            const municipality_id = Number(tariff?.municipality_id);

            return Number.isInteger(municipality_id) && municipality_id > 0 ? municipality_id : "";
        },

        get_selected_tariff_municipality_search(tariff, municipality_id) {
            if (!municipality_id) {
                return this.tariff_form.municipality_search || "";
            }

            return tariff?.municipality_name || this.tariff_form.municipality_search || "";
        },

        apply_tariff_selection(next_form_state, selected_tariff) {
            const municipality_id = this.get_tariff_municipality_id(selected_tariff);

            return ({
                ...next_form_state,
                municipality_id: municipality_id || this.tariff_form.municipality_id || "",
                municipality_search: this.get_selected_tariff_municipality_search(selected_tariff, municipality_id),
                tariff_book_id: selected_tariff?.tariff_book_id || this.tariff_form.tariff_book_id || ""
            });
        },

        apply_basic_form_field(field_name, field_value) {
            const next_form_state = {
                ...this.tariff_form
            };

            if (field_name === "property_id") {
                next_form_state.property_id = field_value;
            }

            if (field_name === "tariff_book_id") {
                next_form_state.tariff_book_id = field_value;
            }

            if (field_name === "tariff_id") {
                next_form_state.tariff_id = field_value;
            }

            if (field_name === "is_active") {
                next_form_state.is_active = field_value;
            }

            return next_form_state;
        },

        update_form_field(field_name, field_value) {
            let next_form_state = this.apply_basic_form_field(field_name, field_value);

            if (field_name === "tariff_book_id") {
                next_form_state.tariff_id = "";
            }

            if (field_name === "tariff_id") {
                next_form_state = this.apply_tariff_selection(
                    next_form_state,
                    this.get_selected_tariff(field_value)
                );
            }

            this.$emit("update:tariffForm", {
                ...next_form_state
            });
        }
    }
};
